
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { searchCustomer } from "@/api/customer";
import { getCNReasonTypes, CreateCNOrder, updateCNOrder } from "@/api/credit-note";
import { COMPANY_LIST } from "@/constant";

@Component({
  name: "creditOrEditNote",
})
export default class extends Vue {
  @Prop({ default: false }) private show!: boolean;
  @Prop({ default: "新建单据" }) private title!: string;
  @Prop({ default: () => ({}) }) private selectedItem!: Record<string, any>;

  private submitting = false;
  private customerLoading = false;
  private customers: any[] = [];
  private reasonOptions: any[] = [];
  private form = this.createEmptyForm();

  private reasonRule = [
    {
      type: "array",
      required: true,
      min: 1,
      message: "请选择原因类型",
      trigger: "change",
    },
  ];

  get formRules() {
    if (this.isEdit) {
      return { reasonType: this.reasonRule };
    }
    return {
      type: [{ required: true, message: "请选择单据类型", trigger: "change" }],
      customerId: [
        { required: true, message: "请输入客人编号或简称", trigger: "change" },
      ],
      company: [{ required: true, message: "请选择交易公司", trigger: "change" }],
      reasonType: this.reasonRule,
    };
  }

  get companies() {
    return COMPANY_LIST;
  }

  get isEdit() {
    return Boolean(this.selectedItem && this.selectedItem.id);
  }

  @Watch("show")
  private onShowChange(visible: boolean) {
    if (!visible) {
      return;
    }
    this.fillForm();
    this.searchCustomer("");
    this.$nextTick(() => {
      const form = this.$refs.ruleForm as any;
      if (form) {
        form.clearValidate();
      }
    });
  }

  created() {
    this.searchCustomer("");
    this.getReasonType();
  }

  private createEmptyForm() {
    return {
      customerId: "",
      type: "CREDIT_NOTE",
      company: "",
      reasonType: [] as string[],
      remark: "",
    };
  }

  @Watch("reasonOptions")
  private onReasonOptionsChange() {
    if (!this.show || !this.isEdit) {
      return;
    }
    const path = this.resolveReasonPath(this.selectedItem.reasonType);
    if (path.length) {
      this.form.reasonType = path;
    }
  }

  private fillForm() {
    if (this.isEdit) {
      const item = this.selectedItem;
      this.form = {
        customerId: item.customer || "",
        type: this.normalizeType(item.type),
        company: this.normalizeCompany(item.company),
        reasonType: this.resolveReasonPath(item.reasonType),
        remark: item.remark || "",
      };
      return;
    }
    this.form = this.createEmptyForm();
  }

  private normalizeType(type: string) {
    if (type === "DEBIT_NOTE") {
      return "DEBIT_NOTE";
    }
    if (type === "CREDIT_NOTE") {
      return "CREDIT_NOTE";
    }
    return type || "";
  }

  private normalizeCompany(company: string) {
    const matched = COMPANY_LIST.find(
      (item) =>
        item.value === company || item.name === company || item.desc === company
    );
    return matched ? matched.value : company || "";
  }

  private resolveReasonPath(raw: any): string[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw === undefined || raw === null || raw === "") {
      return [];
    }
    return this.findReasonPath(this.reasonOptions, String(raw)) || [];
  }

  /** 选项 label 形如「30112 退回（退回运费）」，列表只返回后面的名称 */
  private reasonLabelName(label: string) {
    const text = String(label || "").trim();
    return text.replace(/^\S+\s+/, "");
  }

  private isReasonLabelMatch(label: string, target: string) {
    const text = String(label || "").trim();
    const name = this.reasonLabelName(text);
    return name === target || text === target;
  }

  private findReasonPath(
    nodes: any[],
    target: string,
    trail: string[] = []
  ): string[] | null {
    if (!Array.isArray(nodes)) {
      return null;
    }
    const name = target.trim();
    for (const node of nodes) {
      const next = trail.concat(node.value);
      if (node.children && node.children.length) {
        const found = this.findReasonPath(node.children, name, next);
        if (found) {
          return found;
        }
      }
      if (this.isReasonLabelMatch(node.label, name)) {
        return next;
      }
    }
    return null;
  }

  private close() {
    this.$emit("update:show", false);
  }

  private async submitForm() {
    if (this.submitting) {
      return;
    }
    const form = this.$refs.ruleForm as any;
    this.submitting = true;
    form.validate(async (valid: boolean) => {
      if (!valid) {
        this.submitting = false;
        return;
      }
      const { customerId, type, company, reasonType, remark } = this.form;
      const reason = Array.isArray(reasonType) ? reasonType[1] : "";
      if (!reason) {
        this.submitting = false;
        this.$notify({
          title: "提示",
          message: "请选择到具体原因",
          type: "warning",
          duration: 2000,
        });
        return;
      }
      try {
        if (this.isEdit) {
          await updateCNOrder({
            id: this.selectedItem.id,
            reasonType: reason,
            remark,
          });
        } else {
          await CreateCNOrder({
            customerId,
            type,
            company,
            reasonType: reason,
            remark,
          });
        }
        this.$notify({
          title: "成功",
          message: this.isEdit ? "编辑成功" : "创建成功",
          type: "success",
          duration: 2000,
        });
        this.form = this.createEmptyForm();
        this.close();
        this.$emit("success", false);
      } catch (error) {
        console.error(error);
      } finally {
        this.submitting = false;
      }
    });
  }

  private async searchCustomer(name = "") {
    this.customerLoading = true;
    try {
      const res: any = await searchCustomer({ keyword: name });
      const list = Array.isArray(res) ? res.slice() : [];
      const currentId = this.isEdit ? this.selectedItem.customerId : "";
      if (currentId && !list.some((item) => item.id === currentId)) {
        list.unshift({
          id: currentId,
          abbreviation: this.selectedItem.customer || currentId,
        });
      }
      this.customers = list;
    } catch (error) {
      console.error(error);
      this.customers = [];
    } finally {
      this.customerLoading = false;
    }
  }

  private normalizeReason(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) {
      return [];
    }
    return nodes.map((node) => {
      const children = node.children
        ? this.normalizeReason(node.children)
        : undefined;
      return {
        label: node.label || node.desc || node.name,
        value: node.value || node.name || node.id,
        children: children && children.length ? children : undefined,
      };
    });
  }

  private async getReasonType() {
    const res = await getCNReasonTypes();
    this.reasonOptions = this.normalizeReason(res as any);
  }
}
